import { useRouter } from 'next/router';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData, graphql } from '@packages/gql/generated/shopping';
import { useIntl } from 'react-intl';
import { useQuery } from 'urql';
import { useMemo } from 'react';
import type { GTMEventGlycerinAddToCart } from '@packages/tracking';
import { useTracking } from '@packages/tracking';
import { useAtomValue } from 'jotai';
import { useHashParameters } from '../../../../hooks/useHashParameters';
import { selectedQuantity } from '../../../../atoms';
import { getSkuPart } from '../../../../helpers/getSkuPart';
import { getGlycerinArticleId } from '../../helper/getGlycerinArticleId';
import { getAddToBasketColorString } from './getAddToBasketColorString';
import { getAddToBasketVariantString } from './getAddToBasketVariantString';
import { getGlycerinAvailability } from '../../helper/getGlycerinAvailability';
import { getGlycerinServices } from '../../helper/getGlycerinServices';
import { getGlycerinOrigin } from '../../helper/getGlycerinOrigin';
import type { AddToBasketTrackingParameter } from './types';

const AddToBasketTrackingFragment = graphql(/* GraphQL */ `
  fragment AddToBasketTrackingFragment on Product {
    akl
    addToBasketTrackingAplus: aplus(language: $lang) @skip(if: $skipAplus) {
      ...GetGlycerinServicesFragment
    }
    availability {
      message
      ...GetGlycerinAvailabilityFragment
    }
    brand {
      name
    }
    defaultCategory
    dimensions {
      axisType
      type
      values {
        displayText
        isSelected
      }
    }
    longDescription
    marketcode
    media {
      url
    }
    name
    price {
      currency
      saving {
        value
      }
      value
      valueOld
      ...GetGlycerinServicesCurrencyFragment
    }
    orderNumber
    promotion
    sku
    ...GetGlycerinArticleIdFragment
  }
`);

const BreadcrumbDocument = graphql(/* GraphQL */ `
  query BreadcrumbBasketData($categoryId: ID!, $locale: String!) {
    breadcrumb(category: $categoryId, locale: $locale) {
      breadCrumb {
        name
      }
    }
  }
`);

export type UseAddToBasketTrackingProps = {
  data: FragmentType<typeof AddToBasketTrackingFragment>;
};

export const useAddToBasketTracking = ({ data }: UseAddToBasketTrackingProps) => {
  const unmaskedData = getFragmentData(AddToBasketTrackingFragment, data);
  const {
    akl,
    addToBasketTrackingAplus,
    availability,
    brand,
    defaultCategory,
    dimensions,
    longDescription,
    marketcode,
    media,
    name,
    orderNumber,
    price,
    promotion,
    sku,
  } = unmaskedData;

  const { query, asPath } = useRouter();
  const { category, searchNo } = useHashParameters();
  const { locale } = useIntl();
  const dispatchGTMEvent = useTracking();
  const quantity = useAtomValue(selectedQuantity);

  const categoryId = category || defaultCategory || undefined;

  const [{ data: breadCrumbData }] = useQuery({
    query: BreadcrumbDocument,
    variables: { categoryId: categoryId ?? '', locale: locale! },
    context: useMemo(() => ({ suspense: false }), []),
    pause: !categoryId,
  });

  const breadcrumbs: Array<string> = [];
  if (breadCrumbData?.breadcrumb?.breadCrumb) {
    breadCrumbData.breadcrumb.breadCrumb.forEach((item) => {
      breadcrumbs.push(item.name);
    });
  }
  const color = getAddToBasketColorString(dimensions);
  const variant = getAddToBasketVariantString(dimensions);

  // tracking parameters for atb request
  // implementation according to https://empiriecom.atlassian.net/browse/INFORM-2117
  const getTrackingParameter = (): AddToBasketTrackingParameter | undefined => {
    if ((query && 'nav-q' in query) || searchNo) {
      return {
        source: 'Suche',
      };
    }
    if (categoryId && breadcrumbs) {
      return {
        source: categoryId,
        breadcrumbs,
      };
    }
    return undefined;
  };

  const trackLayerClosed = () => {
    dispatchGTMEvent({
      event: 'ga_event',
      eventAction: 'click',
      eventCategory: 'ATB_continueShopping',
      eventLabel: 'detailview_atb_modal',
    });
  };

  const trackToBasketClick = () => {
    dispatchGTMEvent({
      event: 'ga_event',
      eventAction: 'click',
      eventCategory: 'ATB_toBasket',
      eventLabel: 'detailview_atb_modal',
    });
  };

  const trackToCheckAndOrder = () => {
    dispatchGTMEvent({
      event: 'ga_event',
      eventAction: 'view',
      eventCategory: 'ATB_toCheckAndOrder',
      eventLabel: 'detailview_atb_modal',
    });
  };

  // TODO replace getSkuPart by size from data
  const trackAddToCart = () => {
    dispatchGTMEvent({
      Merkzettel: 'nein',
      ecommerce: {
        add: {
          products: [
            {
              brand: brand?.name,
              category: query['nav-q'] ? `search:${query['nav-q']}` : breadcrumbs?.join('/'),
              description: longDescription || undefined,
              dimension12: price.value > 0 ? 'nein' : 'ja',
              dimension15: getSkuPart(sku, 'Size'),
              dimension16: color,
              dimension18: price.saving?.value! > 0 ? 'ja' : 'nein',
              dimension19: promotion || undefined,
              dimension20: availability.message,
              dimension21: brand?.name,
              id: orderNumber,
              imageUrl: media[0].url || undefined,
              marketcode: marketcode || undefined,
              name,
              price: price.value / 100,
              quantity,
              sku,
              variant,
            },
          ],
        },
      },
      event: 'EECaddToCart',
    });
  };

  const trackCatalogGAddedProduct = () => {
    dispatchGTMEvent({
      event: 'ga_event',
      eventAction: 'viewLayer',
      eventCategory: 'catalog_addedproduct',
      eventValue: {
        orderNumber: sku.concat(promotion || ''),
        price: price.value,
        size: getSkuPart(sku, 'Size'),
      },
    });
  };

  const trackGlycerinAddToCart = () => {
    dispatchGTMEvent<GTMEventGlycerinAddToCart>({
      event: 'AddToCart',
      AddToCartData: {
        addedProducts: [
          {
            ...getGlycerinArticleId(unmaskedData),
            productId: akl,
            skuId: sku,
            quantity,
            currency: price.currency,
            displayedPrice: price.value,
            originalPrice: price.valueOld || undefined,
            ...getGlycerinAvailability(availability),
            ...getGlycerinServices({ aplus: addToBasketTrackingAplus, price }),
            ...getGlycerinOrigin(asPath),
          },
        ],
        passive: false,
      },
    });
  };

  return {
    getTrackingParameter,
    trackLayerClosed,
    trackToBasketClick,
    trackToCheckAndOrder,
    trackAddToCart,
    trackCatalogGAddedProduct,
    trackGlycerinAddToCart,
  };
};
